import styled from '@emotion/styled';
import { BLACK, BLUE_CREEK, COOL_BLUE, COOL_GREY, MIDDLE_GREY, RED_CREEK, WHITE } from '../styles/colors';

const sz = (v?: number | string) =>
  v == null ? undefined : typeof v === 'number' ? `${v}px` : v;

export const Root = styled('div')`
  position: relative;
  display: inline-block;
  overflow: visible !important;
  width: auto;
  --dropdown-font: inherit;
`;

export const Backdrop = styled('div')`
  position: fixed;
  inset: 0;
  z-index: 9998;
  background: transparent;
  pointer-events: auto;
`;

export const Button = styled('button')<{
  controlWidth?: number | string;
  controlMaxWidth?: number | string;
  controlHeight?: number | string;
  iconSize?: number | string;
}>`
  width: ${({ controlWidth }) => sz(controlWidth) || 'auto'};
  max-width: ${({ controlMaxWidth }) => sz(controlMaxWidth) || '100%'};
  height: ${({ controlHeight }) => sz(controlHeight)};

  display: grid;
  grid-template-columns: 1fr ${({ iconSize }) => sz(iconSize) || '24px'};
  align-items: center;
  column-gap: 10px;

  padding: 8px 10px;
  border: 2px solid #90caf9;
  border-radius: 4px;
  background: ${WHITE};
  color: ${BLUE_CREEK};
  cursor: pointer;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #1976d2;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.15);
  }

  &.is-disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ButtonBody = styled('div')`
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-right: 10px;
`;

export const ButtonText = styled('span')<{ isPlaceholder?: boolean }>`
  min-width: 0;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  font-size: 14px;
  line-height: 1.25;
  padding-left: 5px;
  font-weight: 700;

  ${({ isPlaceholder }) =>
    isPlaceholder
      ? `
    color: ${BLUE_CREEK};
    text-transform: uppercase;
  `
      : ''}
`;

export const SearchIcon = styled('span')<{ iconSize?: number | string }>`
  width: ${({ iconSize }) => sz(iconSize) || '24px'};
  height: ${({ iconSize }) => sz(iconSize) || '24px'};
  color: ${BLUE_CREEK};
  display: inline-flex;
  align-items: center;
  justify-content: center;

  & > svg {
    width: 100%;
    height: 100%;
  }
`;

export const Panel = styled('div')<{
  minW?: number;
  prefMinW?: number | string;
  maxW?: number | string;
  panelMaxHeight?: number; // now used by CE (not the panel)
}>`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 9999;

  display: flex;
  flex-direction: column;

  width: max-content;

  ${({ minW, prefMinW }) =>
    prefMinW
      ? `min-width: max(${minW ? `${minW}px` : '0px'}, ${sz(prefMinW)});`
      : `min-width: ${minW ? `${minW}px` : '320px'};`}

  ${({ maxW }) => (maxW ? `max-width: ${sz(maxW)};` : '')}

  /* IMPORTANT: let content control height; CE will scroll itself */
  overflow: visible;

  background: ${WHITE};
  border: 1px solid ${COOL_GREY};
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 8px;
  box-sizing: border-box;
`;

export const Search = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  padding-bottom: 6px;
`;

export const SearchFieldWrapper = styled('div')`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;

  & > input[type='text'] {
    width: 100%;
    max-width: 100%;
    padding: 6px 28px 6px 8px;
    border: 1px solid ${COOL_GREY};
    border-radius: 4px;
    font-size: 14px;
    line-height: 1.4;
    font-weight: 400;
    letter-spacing: 1px;
    box-sizing: border-box;
    outline: none;
    color: ${BLACK};
    background: ${WHITE};

    &::placeholder {
      color: ${MIDDLE_GREY};
      opacity: 1;
    }

    &:focus {
      border-color: ${BLUE_CREEK};
      box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.15);
    }

    &[aria-invalid='true'] {
      border-color: ${RED_CREEK};
      box-shadow: 0 0 0 3px rgba(220, 0, 78, 0.16);
      caret-color: ${RED_CREEK};
    }
  }
`;

/* contentEditable (ID list) */
export const Editable = styled('div')`
  width: 100%;
  max-width: 100%;
  padding: 6px 28px 6px 8px;
  border: 1px solid ${COOL_GREY};
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 400;
  letter-spacing: 1px;
  box-sizing: border-box;
  outline: none;
  color: ${BLACK};
  background: ${WHITE};

  /* Let CE handle its own overflow/scroll */
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;

  &:focus {
    border-color: ${BLUE_CREEK};
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.15);
  }

  &[aria-invalid='true'] {
    border-color: ${RED_CREEK};
    box-shadow: 0 0 0 3px rgba(220, 0, 78, 0.16);
    caret-color: ${RED_CREEK};
  }

  &:empty:before {
    content: attr(data-placeholder);
    color: ${MIDDLE_GREY};
    opacity: 1;
    font-size: inherit;
    line-height: inherit;
    font-weight: inherit;
    letter-spacing: inherit;
  }

  & .invalid {
    color: ${RED_CREEK};
  }
`;

export const ClearButton = styled('button')`
  margin-top: 5px;
  margin-left: auto;
  align-self: flex-start;
  border: none;
  background: transparent;
  color: ${BLUE_CREEK};
  font-size: 13px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  white-space: nowrap;

  &:hover {
    background: ${COOL_BLUE};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const HelperText = styled('div')`
  margin-top: 6px;
  font-size: 12px;
  color: ${RED_CREEK};
  flex: 0 0 auto;
  background: ${WHITE};
  padding: 6px 4px 4px 4px;
  border-top: 1px solid ${COOL_GREY};
`;
